import React, { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../providers/AuthProvider'
import { NavigationItem, NavigationPages } from '../types/Navigation'
import { classNames } from '../lib/utils/classNames'
import Link from 'next/link'
import routes from '../config/routes'
import { HeroIcons } from '../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import LanguageSelect from './LanguageSelect'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from '@headlessui/react'
import BalanceDisplay from './shared/navbar/BalanceDisplay'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { FEATURE_FLAG } from '../types/FeatureFlag'

const buttonStyle = {
  active: 'bg-purpleGradient hover:shadow-lg',
  inactive: 'bg-white hover:bg-brandLightPurple/20',
}

const buttonTextStyle = {
  active: 'text-neutralWhite',
  inactive: 'text-sheltersBlue',
}

const buttonIconStyle = {
  active: 'text-neutralWhite',
  inactive: 'text-sheltersBlue',
}

interface NavbarProps {
  navigation: NavigationItem[]
}

export default function Navbar({ navigation }: NavbarProps) {
  const { t } = useTranslation('common')
  const ffWalletBalance = useFeatureFlag(FEATURE_FLAG.WALLET_BALANCE)
  const { isLoggedIn, isLoading, logout } = useAuth()
  const visibleNavigation = navigation.filter(
    (navItem) => isLoggedIn || navItem.id !== NavigationPages.ACCOUNT,
  )

  const disclosureRef = useRef<HTMLDivElement>(null)
  const closeDisclosure = useCallback(() => {
    const openDisclosureButton = disclosureRef.current?.querySelector(
      'button[aria-expanded="true"]',
    ) as HTMLElement | null
    openDisclosureButton?.click()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        disclosureRef.current &&
        !disclosureRef.current.contains(event.target as Node)
      ) {
        closeDisclosure()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeDisclosure])

  return (
    <div className="relative z-40 w-full grow" ref={disclosureRef}>
      <Disclosure as="div">
        {({ open }) => (
          <>
            <nav
              aria-label={t('navbar.mainNavigation')}
              className="border-1 relative z-40 w-full rounded-2xl border-sheltersBlue/20 bg-neutralWhite p-2.5 shadow"
            >
              <div className="flex items-center">
                <Link
                  href={routes.index}
                  className="hidden items-center lg:flex"
                >
                  <img
                    src="/img/logos/shelters/logo-cropped-colours.svg"
                    alt="shelters logo"
                    className="h-[34px] w-[48px]"
                  />
                  <img
                    src="/img/logos/shelters/logotype-cropped-colours.svg"
                    alt="shelters logotype"
                    className="ml-3 block h-[24px] w-[100vh] lg:hidden lg:w-auto 2xl:block"
                  />
                </Link>

                <div className="flex w-full items-center gap-2 lg:hidden">
                  <DisclosureButton
                    aria-label={open ? 'Close navigation' : 'Open navigation'}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-sheltersBlue/10"
                  >
                    {open ? (
                      <HeroIcons.XMarkIcon className="h-8 w-8 text-sheltersBlue" />
                    ) : (
                      <HeroIcons.Bars3Icon className="h-8 w-8 text-sheltersBlue" />
                    )}
                  </DisclosureButton>

                  <Link
                    href={routes.index}
                    className="flex min-w-0 shrink items-center"
                  >
                    <img
                      src="/img/logos/shelters/logotype-cropped-colours.svg"
                      alt="shelters logotype"
                      className="h-7 w-auto max-w-[88px] sm:max-w-[116px]"
                    />
                  </Link>

                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    <LanguageSelect minimalDisplay />
                    {!isLoading && (
                      <>
                        {!isLoggedIn ? (
                          <Link
                            href={routes.register}
                            className="flex min-h-11 items-center justify-center whitespace-nowrap rounded-md bg-purpleGradient px-3 text-sm font-medium text-white shadow-sm hover:shadow-lg"
                          >
                            {t('navbar.register')}
                          </Link>
                        ) : ffWalletBalance ? (
                          <BalanceDisplay onClick={closeDisclosure} />
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                <div className="hidden grow items-center justify-center space-x-3 lg:flex">
                  {visibleNavigation.map((navItem, index) => (
                    <Link
                      href={navItem.href}
                      key={index}
                      prefetch={true}
                      className={classNames(
                        'group flex w-40 items-center justify-center rounded-md px-3 py-2',
                        'min-h-11',
                        navItem.isActive
                          ? buttonStyle.active
                          : buttonStyle.inactive,
                      )}
                    >
                      <div className="flex items-center">
                        <navItem.icon
                          className={classNames(
                            'mr-2 h-5 w-5',
                            navItem.isActive
                              ? buttonIconStyle.active
                              : buttonIconStyle.inactive,
                          )}
                        />
                        <p
                          className={classNames(
                            'text-sm font-normal leading-none',
                            navItem.isActive
                              ? buttonTextStyle.active
                              : buttonTextStyle.inactive,
                          )}
                        >
                          {navItem.name}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="hidden items-center space-x-1.5 lg:flex">
                  {ffWalletBalance && isLoggedIn && !isLoading && (
                    <BalanceDisplay onClick={closeDisclosure} />
                  )}
                  <LanguageSelect minimalDisplay={true} />
                  {!isLoading && (
                    <>
                      {!isLoggedIn ? (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Link
                              href={routes.login}
                              className="group flex min-h-11 items-center justify-center overflow-hidden rounded-md px-3 py-1.5 hover:bg-sheltersBlue/20 group-hover:cursor-pointer"
                            >
                              <p className="text-sm font-medium text-sheltersBlue">
                                {t('navbar.login')}
                              </p>
                            </Link>
                            <Link
                              href={routes.register}
                              className="group flex items-center justify-center overflow-hidden rounded-md bg-purpleGradient px-3 py-1.5 shadow-sm hover:shadow-lg group-hover:cursor-pointer"
                            >
                              <span className="relative h-8 w-8 overflow-hidden rounded-full bg-white/20">
                                <HeroIcons.UserPlusIcon className="absolute h-8 w-8 p-1.5 text-white transition-opacity duration-500 group-hover:animate-launchDisappear group-hover:opacity-0" />
                                <HeroIcons.RocketLaunchIcon className="absolute mx-auto h-8 w-8 p-1.5 text-white opacity-0 group-hover:animate-launch group-hover:opacity-100" />
                              </span>
                              <p className="ml-2 text-sm font-medium text-white">
                                {t('navbar.register')}
                              </p>
                            </Link>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center">
                            <button
                              onClick={logout}
                              className="group flex min-h-11 items-center justify-center overflow-hidden rounded-md px-3 py-1.5 hover:bg-sheltersBlue/20 group-hover:cursor-pointer"
                            >
                              <p className="mr-1 text-sm text-sheltersBlue">
                                {t('navbar.logout')}
                              </p>
                              <HeroIcons.ArrowRightOnRectangleIcon className="h-5 w-5 text-sheltersBlue" />
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              <Transition
                enter="transition-transform duration-200 ease-out"
                enterFrom="-translate-y-2 opacity-0"
                enterTo="translate-y-0 opacity-100"
                leave="transition-transform duration-150 ease-in"
                leaveFrom="translate-y-0 opacity-100"
                leaveTo="-translate-y-2 opacity-0"
              >
                <DisclosurePanel className="absolute left-0 right-0 top-full z-30 mt-1 block w-full rounded-2xl border border-sheltersBlue/10 bg-neutralWhite p-2.5 text-gray-500 lg:hidden">
                  <div className="flex flex-col space-y-2.5 border-b pb-2.5">
                    {visibleNavigation.map((navItem, index) => (
                      <DisclosureButton
                        as={Link}
                        href={navItem.href}
                        key={index}
                        prefetch={true}
                        className={classNames(
                          'group flex items-center rounded-md px-2 py-1',
                          'min-h-9',
                          navItem.isActive
                            ? buttonStyle.active
                            : buttonStyle.inactive,
                        )}
                      >
                        <navItem.icon
                          className={classNames(
                            'mr-2 h-5 w-5',
                            navItem.isActive
                              ? buttonIconStyle.active
                              : buttonIconStyle.inactive,
                          )}
                        />
                        <p
                          className={classNames(
                            'text-sm font-normal leading-none',
                            navItem.isActive
                              ? buttonTextStyle.active
                              : buttonTextStyle.inactive,
                          )}
                        >
                          {navItem.name}
                        </p>
                      </DisclosureButton>
                    ))}
                  </div>

                  {!isLoading && (
                    <div className="flex items-center pt-2.5">
                      <div className="grow"></div>
                      {!isLoggedIn ? (
                        <Link
                          href={routes.login}
                          className="group flex min-h-9 items-center justify-center overflow-hidden rounded-md px-3 py-1.5 hover:bg-sheltersBlue/20 group-hover:cursor-pointer"
                        >
                          <p className="text-sm font-medium text-sheltersBlue">
                            {t('navbar.login')}
                          </p>
                        </Link>
                      ) : (
                        <button
                          onClick={logout}
                          className="group flex min-h-9 items-center justify-center overflow-hidden rounded-md px-3 py-1.5 hover:bg-sheltersBlue/20 group-hover:cursor-pointer"
                        >
                          <p className="mr-1 text-sm text-sheltersBlue">
                            {t('navbar.logout')}
                          </p>
                          <HeroIcons.ArrowRightOnRectangleIcon className="h-5 w-5 text-sheltersBlue" />
                        </button>
                      )}
                    </div>
                  )}
                </DisclosurePanel>
              </Transition>
            </nav>
          </>
        )}
      </Disclosure>
    </div>
  )
}
